import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../utils/constants';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const apiGet = async (path) => {
  const res = await fetch(`${API_URL}${path}`, { headers: authHeaders() });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw Object.assign(new Error(e.detail || `Error ${res.status}`), { status: res.status }); }
  return res.json();
};
const apiPost = async (path, body = {}) => {
  const fd = new FormData(); Object.entries(body).forEach(([k, v]) => fd.append(k, v));
  const res = await fetch(`${API_URL}${path}`, { method: 'POST', headers: authHeaders(), body: fd });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw Object.assign(new Error(e.detail || `Error ${res.status}`), { status: res.status }); }
  return res.json();
};
const apiPut = async (path, body = {}) => {
  const fd = new FormData(); Object.entries(body).forEach(([k, v]) => fd.append(k, v));
  const res = await fetch(`${API_URL}${path}`, { method: 'PUT', headers: authHeaders(), body: fd });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || `Error ${res.status}`); }
  return res.json();
};

// ── Design tokens — matched to homepage aesthetic ─────────────────────────
const C = {
  bg:        '#080d18',
  nav:       '#0a1020',
  surface:   '#0f172a',
  surface2:  '#111d35',
  surfaceHover: '#162040',
  border:    '#1e3a5f',
  borderDim: '#172035',
  teal:      '#22d3ee',
  tealDark:  '#06b6d4',
  blue:      '#6366f1',
  blueDark:  '#4f46e5',
  text:      '#e2e8f0',
  muted:     '#64748b',
  faint:     '#1e293b',
  critical:  '#ef4444',
  high:      '#f59e0b',
  medium:    '#3b82f6',
  low:       '#10b981',
  white:     '#ffffff',
};

// Gradient matched to homepage CTA button
const GRAD = `linear-gradient(135deg, ${C.teal} 0%, ${C.blue} 100%)`;
const GRAD_HOVER = `linear-gradient(135deg, ${C.tealDark} 0%, ${C.blueDark} 100%)`;

const SEV_COLOR = { critical: C.critical, high: C.high, medium: C.medium, low: C.low };
const scoreColor = (s) => s >= 80 ? C.low : s >= 60 ? C.high : C.critical;

const humaniseType = (raw = '') => {
  const map = {
    SECURITY_GROUP_OPEN: 'Open Security Group',
    S3_PUBLIC_ACL: 'Public S3 Bucket',
    CLOUDTRAIL_NOT_ENABLED: 'CloudTrail Disabled',
    CLOUDTRAIL_NOT_MULTI_REGION: 'CloudTrail Single Region',
    CLOUDTRAIL_LOGGING_DISABLED: 'CloudTrail Logging Off',
    IAM_USER_NO_MFA: 'IAM MFA Disabled',
    ROOT_MFA_DISABLED: 'Root MFA Disabled',
    EBS_UNENCRYPTED: 'Unencrypted EBS',
    S3_NO_LIFECYCLE: 'S3 No Lifecycle',
    LAMBDA_RIGHTSIZING: 'Lambda Overprovisioned',
    EC2_RIGHTSIZING_MODIFY: 'EC2 Overprovisioned',
    SAVINGS_PLAN_OPPORTUNITY: 'Savings Plan Available',
    INTELLIGENT_TIERING: 'S3 Intelligent Tiering',
    IAM_STALE_ACCESS_KEY: 'Stale IAM Key',
  };
  return map[raw.toUpperCase()] || raw.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

// ── Shared style helpers ──────────────────────────────────────────────────
const card = (extra = {}) => ({
  background: C.surface,
  border: `1px solid ${C.border}`,
  borderRadius: 16,
  padding: 24,
  marginBottom: 18,
  boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
  ...extra,
});

const gradBtn = (extra = {}) => ({
  background: GRAD,
  color: C.white,
  border: 'none',
  borderRadius: 10,
  padding: '11px 24px',
  cursor: 'pointer',
  fontWeight: 700,
  fontSize: 14,
  fontFamily: 'Inter, sans-serif',
  letterSpacing: '0.01em',
  boxShadow: `0 0 20px rgba(34,211,238,0.25)`,
  transition: 'all 0.2s',
  ...extra,
});

const ghostBtn = (extra = {}) => ({
  background: 'transparent',
  color: C.muted,
  border: `1px solid ${C.border}`,
  borderRadius: 10,
  padding: '11px 22px',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: 14,
  fontFamily: 'Inter, sans-serif',
  transition: 'all 0.2s',
  ...extra,
});

const inp = {
  background: C.surface,
  border: `1px solid #334155`,
  borderRadius: 8,
  padding: '10px 14px',
  color: C.text,
  fontSize: 14,
  width: '100%',
  boxSizing: 'border-box',
  marginBottom: 12,
  fontFamily: 'Inter, sans-serif',
  outline: 'none',
};

const fieldLabel = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: C.muted,
  marginBottom: 5,
  letterSpacing: '0.04em',
};

const sevPill = (sev = 'low') => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  background: (SEV_COLOR[sev] || C.muted) + '18',
  color: SEV_COLOR[sev] || C.muted,
  border: `1px solid ${(SEV_COLOR[sev] || C.muted)}40`,
  borderRadius: 6,
  padding: '3px 10px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
  fontFamily: 'monospace',
});

// ── Score Arc SVG ────────────────────────────────────────────────────────
const ScoreArc = ({ score = 0 }) => {
  const r = 46, cx = 56, cy = 56;
  const circ = Math.PI * r;
  const dash = (score / 100) * circ;
  const col = scoreColor(score);
  return (
    <svg width={112} height={66} viewBox="0 0 112 66">
      <defs>
        <linearGradient id="arcGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={C.teal} />
          <stop offset="100%" stopColor={C.blue} />
        </linearGradient>
      </defs>
      <path d={`M 10 56 A ${r} ${r} 0 0 1 102 56`} fill="none" stroke={C.faint} strokeWidth={9} strokeLinecap="round" />
      <path d={`M 10 56 A ${r} ${r} 0 0 1 102 56`} fill="none" stroke={score > 0 ? 'url(#arcGrad)' : col} strokeWidth={9}
        strokeLinecap="round" strokeDasharray={`${dash} ${circ}`}
        style={{ transition: 'stroke-dasharray 1s ease' }} />
      <text x={cx} y={52} textAnchor="middle" fill={col} fontSize={20} fontWeight={800} fontFamily="Inter, sans-serif">{score}</text>
      <text x={cx} y={63} textAnchor="middle" fill={C.muted} fontSize={10} fontFamily="monospace">/100</text>
    </svg>
  );
};

// ── Live badge ───────────────────────────────────────────────────────────
const LiveBadge = () => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: C.teal + '15', border: `1px solid ${C.teal}40`, borderRadius: 20, padding: '2px 10px' }}>
    <style>{`@keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.5;transform:scale(0.85)}}`}</style>
    <span style={{ width: 7, height: 7, borderRadius: '50%', background: C.teal, boxShadow: `0 0 8px ${C.teal}`, animation: 'pulse 2s infinite', display: 'block' }} />
    <span style={{ fontSize: 11, color: C.teal, fontWeight: 700, letterSpacing: '0.08em' }}>LIVE</span>
  </span>
);

// ── Spinner ──────────────────────────────────────────────────────────────
const Spinner = ({ size = 28 }) => (
  <>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    <div style={{ width: size, height: size, border: `3px solid ${C.faint}`, borderTop: `3px solid ${C.teal}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
  </>
);

// ── FIXED DATE FUNCTIONS ─────────────────────────────────────────────────
const userTZ = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const fmtDateTime = (iso) => {
  if (!iso) return 'N/A';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: userTZ(),
      hour12: false
    });
  } catch (e) {
    return 'Invalid date';
  }
};

const fmtDateShort = (iso) => {
  if (!iso) return 'N/A';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: userTZ(),
      hour12: false
    });
  } catch (e) {
    return 'Invalid date';
  }
};

const fmtDateOnly = (iso) => {
  if (!iso) return 'N/A';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: userTZ()
    });
  } catch (e) {
    return 'Invalid date';
  }
};

// ════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════
const DashboardPage = () => {
  const [tab, setTab]             = useState('overview');
  const [user, setUser]           = useState(null);
  const [scan, setScan]           = useState(null);
  const [history, setHistory]     = useState([]);
  const [billing, setBilling]     = useState(null);
  const [invoices, setInvoices]   = useState([]);
  const [awsInfo, setAwsInfo]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [scanning, setScanning]   = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [histPdfLoading, setHistPdfLoading] = useState({});
  const [error, setError]         = useState('');
  const [msg, setMsg]             = useState('');
  const [roleArn, setRoleArn]     = useState('');
  const [slackUrl, setSlackUrl]   = useState('');
  const [accForm, setAccForm]     = useState({ full_name: '', company: '' });
  const [pwForm, setPwForm]       = useState({ old_password: '', new_password: '' });
  const [expandedFinding, setExpandedFinding] = useState(null);
  const navigate = useNavigate();

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 6000); };

  const handleExportData = async () => {
  try {
    const res = await fetch(`${API_URL}/account/export`, { headers: authHeaders() });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Export failed'); }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `spectraine-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) { flash(`❌ Export failed: ${e.message}`); }
};

  const loadAll = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/'); return; }
    try {
      const [u, b, a] = await Promise.all([
        apiGet('/me'),
        apiGet('/billing/status'),
        apiGet('/aws/setup-info').catch(() => null),
      ]);
      setUser(u); setBilling(b); setAwsInfo(a);
      setAccForm({ full_name: u.full_name || '', company: u.company || '' });
      if (u.subscription_active) {
        try { const s = await apiGet('/scan/latest'); setScan(s); } catch (_) {}
        try { const h = await apiGet('/scans/history?months=6'); setHistory(h.scans || []); } catch (_) {}
        try { const inv = await apiGet('/billing/invoices'); setInvoices(Array.isArray(inv) ? inv : []); } catch (_) {}
      }
    } catch (e) {
      if (e.status === 401) { localStorage.clear(); navigate('/'); return; }
      if (e.status === 402) { navigate('/pricing'); return; }
      setError('Failed to load dashboard. Please refresh.');
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    if (!scanning) return;
    const t = setInterval(loadAll, 30000);
    return () => clearInterval(t);
  }, [scanning, loadAll]);

  const handleRunScan = async () => {
    if (!user?.aws_role_arn) { flash('⚠️ Connect your AWS account first under AWS Setup'); return; }
    setScanning(true);
    flash('🔍 Scan running — this typically takes 1–2 minutes. Page will auto-refresh.');
    try {
      await apiPost('/scan/run');
      const s = await apiGet('/scan/latest');
      setScan(s);
      flash('✅ Scan complete!');
    } catch (e) { flash(`❌ ${e.message}`); }
    finally { setScanning(false); }
  };

  const handleDownloadPDF = async (scanId, isHistory = false, idx = null) => {
    if (isHistory && idx !== null) setHistPdfLoading(p => ({ ...p, [idx]: true }));
    else setPdfLoading(true);
    try {
      const res = await fetch(`${API_URL}/scan/${scanId}/pdf`, { headers: authHeaders() });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'PDF failed'); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `spectraine-report-${new Date().toISOString().slice(0, 10)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(`Report download failed: ${e.message}`); }
    finally {
      if (isHistory && idx !== null) setHistPdfLoading(p => ({ ...p, [idx]: false }));
      else setPdfLoading(false);
    }
  };

  const handleConnectAws = async (e) => {
    e.preventDefault();
    try { await apiPost('/aws/connect', { role_arn: roleArn }); flash('✅ AWS role connected!'); await loadAll(); }
    catch (e) { flash(`❌ ${e.message}`); }
  };
  const handleSlack = async (e) => {
    e.preventDefault();
    try { await apiPost('/aws/update-slack', { webhook_url: slackUrl }); flash('✅ Slack webhook saved!'); }
    catch (e) { flash(`❌ ${e.message}`); }
  };
  const handleUpdateAccount = async (e) => {
    e.preventDefault();
    try { await apiPut('/account/update', accForm); flash('✅ Account updated!'); await loadAll(); }
    catch (e) { flash(`❌ ${e.message}`); }
  };
  const handleChangePassword = async (e) => {
    e.preventDefault();
    try { await apiPut('/account/change-password', pwForm); flash('✅ Password changed!'); setPwForm({ old_password: '', new_password: '' }); }
    catch (e) { flash(`❌ ${e.message}`); }
  };
  const handleSubscribe = async () => {
    try { const d = await apiPost('/billing/create-checkout-session', { plan: 'monthly' }); window.location.href = d.checkout_url; }
    catch (e) { flash(`❌ ${e.message}`); }
  };
  const handleBillingPortal = async () => {
    try { const d = await apiPost('/billing/portal'); window.location.href = d.portal_url; }
    catch (e) { flash(`❌ ${e.message}`); }
  };
  const handleLogout = () => { localStorage.removeItem('token'); localStorage.removeItem('user'); navigate('/'); };

  // ── Loading screen ──────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
      <Spinner size={36} />
      <p style={{ color: C.muted, fontFamily: 'Inter, sans-serif', fontSize: 14, margin: 0 }}>Loading Spectraine...</p>
    </div>
  );

  const findings = (scan?.findings || []).map(f => ({ ...f, severity: (f.severity || 'low').toLowerCase() }));
  const scoreCol = scoreColor(scan?.security_score || 0);
  // ── FIXED scanDate ─────────────────────────────────────────────────────
  const scanDate = scan?.created_at ? fmtDateTime(scan.created_at) : null;

  const TABS = [
    { id: 'overview',   label: 'Overview' },
    { id: 'findings',   label: `Findings${findings.length ? ` (${findings.length})` : ''}` },
    { id: 'cost',       label: 'Cost' },
    { id: 'compliance', label: 'Compliance' },
    { id: 'history',    label: 'History' },
    { id: 'aws',        label: 'AWS Setup' },
    { id: 'settings',   label: 'Settings' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: 'Inter, -apple-system, sans-serif' }}>

      {/* ── Navbar ─────────────────────────────────────────────────────── */}
      <nav style={{ background: C.nav, borderBottom: `1px solid ${C.border}`, padding: '0 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 58, position: 'sticky', top: 0, zIndex: 100, backdropFilter: 'blur(12px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          {/* Logo — matches homepage */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V7L12 2z" stroke={C.teal} strokeWidth="2" strokeLinejoin="round" />
              <path d="M9 12l2 2 4-4" stroke={C.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span style={{ fontWeight: 800, fontSize: 17, letterSpacing: '0.06em', background: GRAD, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>SPECTRAINE</span>
          </div>
          {scanDate && (
            <span style={{ fontSize: 12, color: C.muted, display: 'flex', alignItems: 'center', gap: 8 }}>
              {scan?.aws_account_id && <span><span style={{ color: C.faint + 'cc' }}>ACCT</span> <span style={{ color: C.muted }}>{scan.aws_account_id}</span> ·</span>}
              <span><span style={{ color: C.faint + 'cc' }}>LAST SCAN</span> <span style={{ color: C.muted }}>{scanDate}</span></span>
            </span>
          )}
          <LiveBadge />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 13, color: C.muted }}>{user?.email}</span>
          <button onClick={handleLogout} style={ghostBtn({ padding: '7px 16px', fontSize: 13, borderRadius: 8 })}>Sign out</button>
        </div>
      </nav>

      {/* ── Flash messages ────────────────────────────────────────────── */}
      {msg && (
        <div style={{ background: C.teal + '12', borderBottom: `1px solid ${C.teal}30`, padding: '10px 32px', fontSize: 13, color: C.teal }}>
          {msg}
        </div>
      )}
      {error && (
        <div style={{ background: C.critical + '12', borderBottom: `1px solid ${C.critical}30`, padding: '10px 32px', fontSize: 13, color: C.critical, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {error}
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', color: C.critical, cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>
      )}

      {/* ── Tab bar ───────────────────────────────────────────────────── */}
      <div style={{ background: C.nav, borderBottom: `1px solid ${C.border}`, padding: '0 32px', display: 'flex', gap: 2, overflowX: 'auto' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '13px 18px',
            fontSize: 14, fontWeight: tab === t.id ? 700 : 500,
            fontFamily: 'Inter, sans-serif',
            color: tab === t.id ? C.teal : C.muted,
            borderBottom: tab === t.id ? `2px solid ${C.teal}` : '2px solid transparent',
            whiteSpace: 'nowrap',
            transition: 'color 0.15s',
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── Main content ──────────────────────────────────────────────── */}
      <div style={{ maxWidth: 1240, margin: '0 auto', padding: '28px 32px' }}>

        {/* ══════════════════════════════════════════════════════════════
            OVERVIEW
        ══════════════════════════════════════════════════════════════ */}
        {tab === 'overview' && (
          <div>
            {!user?.subscription_active ? (
              <div style={card({ textAlign: 'center', padding: '60px 40px', background: 'linear-gradient(135deg, #0f172a 0%, #0a1628 100%)' })}>
                <div style={{ display: 'inline-block', background: GRAD, borderRadius: 20, padding: '3px 14px', fontSize: 11, fontWeight: 700, color: C.white, letterSpacing: '0.08em', marginBottom: 20 }}>SUBSCRIPTION REQUIRED</div>
                <h2 style={{ color: C.white, margin: '0 0 14px', fontSize: 26, fontWeight: 800 }}>Unlock Spectraine Professional</h2>
                <p style={{ color: C.muted, maxWidth: 500, margin: '0 auto 28px', lineHeight: 1.7, fontSize: 15 }}>
                  Daily AWS security scans, cost intelligence, PDF reports and compliance posture — connected in 2 minutes.
                </p>
                <button onClick={handleSubscribe} style={gradBtn({ padding: '13px 36px', fontSize: 15 })}>Subscribe — $497/month</button>
              </div>
            ) : !user?.aws_role_arn ? (
              <div style={card({ textAlign: 'center', padding: '60px 40px' })}>
                <div style={{ display: 'inline-block', background: GRAD, borderRadius: 20, padding: '3px 14px', fontSize: 11, fontWeight: 700, color: C.white, letterSpacing: '0.08em', marginBottom: 20 }}>SETUP REQUIRED</div>
                <h2 style={{ color: C.white, margin: '0 0 14px', fontSize: 26, fontWeight: 800 }}>Connect your AWS Account</h2>
                <p style={{ color: C.muted, maxWidth: 500, margin: '0 auto 28px', lineHeight: 1.7 }}>Connect via CloudFormation in under 2 minutes. We never store your credentials.</p>
                <button onClick={() => setTab('aws')} style={gradBtn({ padding: '13px 36px', fontSize: 15 })}>Go to AWS Setup →</button>
              </div>
            ) : (
              <>
                {/* KPI strip */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>

                  {/* Score card */}
                  <div style={{ ...card({ marginBottom: 0, padding: '20px 16px', textAlign: 'center', position: 'relative', overflow: 'hidden' }), borderTop: `3px solid ${scoreCol}` }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 60, background: `radial-gradient(ellipse at 50% 0%, ${scoreCol}15 0%, transparent 70%)`, pointerEvents: 'none' }} />
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, letterSpacing: '0.07em', marginBottom: 8 }}>SECURITY SCORE</div>
                    <ScoreArc score={scan?.security_score || 0} />
                    <div style={{ fontSize: 12, color: scoreCol, fontWeight: 700, marginTop: 4, letterSpacing: '0.05em' }}>
                      {(scan?.security_score || 0) >= 80 ? 'STRONG' : (scan?.security_score || 0) >= 60 ? 'MODERATE' : 'CRITICAL RISK'}
                    </div>
                  </div>

                  {/* Critical */}
                  <div style={{ ...card({ marginBottom: 0, padding: '22px 20px', position: 'relative', overflow: 'hidden' }), borderTop: `3px solid ${C.critical}` }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 60, background: `radial-gradient(ellipse at 50% 0%, ${C.critical}12 0%, transparent 70%)`, pointerEvents: 'none' }} />
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, letterSpacing: '0.07em', marginBottom: 10 }}>CRITICAL FINDINGS</div>
                    <div style={{ fontSize: 40, fontWeight: 800, color: (scan?.critical_findings || 0) > 0 ? C.critical : C.low, lineHeight: 1, marginBottom: 8 }}>{scan?.critical_findings || 0}</div>
                    <div style={{ fontSize: 12, color: C.muted }}>{scan?.high_findings || 0} high · {scan?.medium_findings || 0} medium · {scan?.low_findings || 0} low</div>
                  </div>

                  {/* Savings */}
                  <div style={{ ...card({ marginBottom: 0, padding: '22px 20px', position: 'relative', overflow: 'hidden' }), borderTop: `3px solid ${C.low}` }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 60, background: `radial-gradient(ellipse at 50% 0%, ${C.low}12 0%, transparent 70%)`, pointerEvents: 'none' }} />
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, letterSpacing: '0.07em', marginBottom: 10 }}>EST. MONTHLY SAVINGS</div>
                    <div style={{ fontSize: 36, fontWeight: 800, color: C.low, lineHeight: 1, marginBottom: 8 }}>${(scan?.monthly_savings || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}</div>
                    <div style={{ fontSize: 12, color: C.muted }}>${((scan?.monthly_savings || 0) * 12).toLocaleString('en-GB', { maximumFractionDigits: 0 })} annualised</div>
                  </div>

                  {/* Threats */}
                  <div style={{ ...card({ marginBottom: 0, padding: '22px 20px', position: 'relative', overflow: 'hidden' }), borderTop: `3px solid ${C.teal}` }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 60, background: `radial-gradient(ellipse at 50% 0%, ${C.teal}12 0%, transparent 70%)`, pointerEvents: 'none' }} />
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, letterSpacing: '0.07em', marginBottom: 10 }}>THREATS BLOCKED</div>
                    <div style={{ fontSize: 40, fontWeight: 800, color: C.teal, lineHeight: 1, marginBottom: 8 }}>{scan?.threats_blocked || 0}</div>
                    <div style={{ fontSize: 12, color: C.muted }}>via active monitoring</div>
                  </div>
                </div>

                {/* Action row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                  <button onClick={handleRunScan} disabled={scanning} style={gradBtn({ opacity: scanning ? 0.65 : 1, display: 'flex', alignItems: 'center', gap: 8 })}>
                    {scanning ? <><Spinner size={16} /> Scanning...</> : '▶ Run Scan'}
                  </button>
                  {scan?.scan_id && (
                    <button onClick={() => handleDownloadPDF(scan.scan_id)} disabled={pdfLoading} style={ghostBtn()}>
                      {pdfLoading ? 'Generating...' : '↓ Download PDF Report'}
                    </button>
                  )}
                  {scanning && <span style={{ fontSize: 12, color: C.muted }}>Auto-refreshing every 30s...</span>}
                </div>

                {/* Executive summary */}
                {scan?.executive_summary?.headline && (
                  <div style={card({ borderLeft: `3px solid ${C.teal}`, padding: '16px 20px', background: C.surface2 })}>
                    <div style={{ fontSize: 11, color: C.teal, fontWeight: 700, letterSpacing: '0.08em', marginBottom: 6 }}>EXECUTIVE SUMMARY</div>
                    <p style={{ margin: 0, fontSize: 14, color: C.text, lineHeight: 1.7 }}>{scan.executive_summary.headline}</p>
                    {scan.executive_summary.roi_multiple > 0 && (
                      <div style={{ marginTop: 8, fontSize: 12, color: C.low, fontWeight: 600 }}>ROI multiple: {scan.executive_summary.roi_multiple}×</div>
                    )}
                  </div>
                )}

                {!scan && (
                  <div style={card({ textAlign: 'center', padding: 48 })}>
                    <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }}>◎</div>
                    <p style={{ color: C.muted, margin: 0 }}>No scan data yet — run your first scan above.</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            FINDINGS
        ══════════════════════════════════════════════════════════════ */}
        {tab === 'findings' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18 }}>
              <div>
                <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700, color: C.white }}>Security Findings</h2>
                <p style={{ margin: 0, fontSize: 13, color: C.muted }}>
                  {findings.length} total · {findings.filter(f => f.severity === 'critical').length} critical · {findings.filter(f => f.severity === 'high').length} high
                </p>
              </div>
              {scan?.scan_id && <button onClick={() => handleDownloadPDF(scan.scan_id)} style={ghostBtn({ fontSize: 13 })}>↓ PDF Report</button>}
            </div>

            {findings.length === 0 ? (
              <div style={card({ textAlign: 'center', padding: 48 })}>
                <p style={{ color: C.muted, margin: 0 }}>No findings — run a scan from the Overview tab.</p>
              </div>
            ) : (
              <div style={{ ...card({ padding: 0, overflow: 'hidden' }) }}>
                {/* Header row */}
                <div style={{ display: 'grid', gridTemplateColumns: '115px 165px 1fr 100px', background: C.surface2, borderBottom: `1px solid ${C.border}`, padding: '10px 18px' }}>
                  {['SEVERITY', 'TYPE', 'RESOURCE / DESCRIPTION', 'REGION'].map(h => (
                    <div key={h} style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.07em' }}>{h}</div>
                  ))}
                </div>

                {findings.map((f, i) => {
                  const sev = f.severity || 'low';
                  const open = expandedFinding === i;
                  return (
                    <div key={i} onClick={() => setExpandedFinding(open ? null : i)} style={{ borderBottom: `1px solid ${C.borderDim}`, cursor: 'pointer', background: open ? C.surface2 : (i % 2 === 0 ? C.surface : C.bg), transition: 'background 0.1s' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '115px 165px 1fr 100px', padding: '11px 18px', alignItems: 'center' }}>
                        <div><span style={sevPill(sev)}><span style={{ width: 6, height: 6, borderRadius: '50%', background: SEV_COLOR[sev] || C.muted, display: 'inline-block' }} />{sev}</span></div>
                        <div style={{ fontSize: 13, color: C.text }}>{humaniseType(f.type || '')}</div>
                        <div style={{ fontSize: 13, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 12 }}>{f.resource || f.title || f.description || '—'}</div>
                        <div style={{ fontSize: 12, color: C.muted, fontFamily: 'monospace' }}>{f.region || '—'}</div>
                      </div>
                      {open && (
                        <div style={{ padding: '0 18px 16px', borderTop: `1px solid ${C.border}` }}>
                          {f.message && <p style={{ margin: '10px 0 8px', fontSize: 13, color: C.muted, lineHeight: 1.7 }}>{f.message}</p>}
                          {f.recommendation && (
                            <div style={{ background: C.teal + '0d', border: `1px solid ${C.teal}30`, borderLeft: `3px solid ${C.teal}`, borderRadius: 8, padding: '10px 14px', marginTop: 8 }}>
                              <div style={{ fontSize: 11, color: C.teal, fontWeight: 700, letterSpacing: '0.07em', marginBottom: 5 }}>RECOMMENDATION</div>
                              <p style={{ margin: 0, fontSize: 13, color: C.text, lineHeight: 1.7 }}>{f.recommendation}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            COST
        ══════════════════════════════════════════════════════════════ */}
        {tab === 'cost' && (
          <div>
            <div style={{ marginBottom: 18 }}>
              <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700, color: C.white }}>Cost Optimisation</h2>
              <p style={{ margin: 0, fontSize: 13, color: C.muted }}>
                Est. savings: <span style={{ color: C.low, fontWeight: 700 }}>${(scan?.monthly_savings || 0).toLocaleString()}/month</span>
              </p>
            </div>
            {(() => {
              const costKeys = ['S3_NO_LIFECYCLE','INTELLIGENT_TIERING','LAMBDA_RIGHTSIZING','EC2_RIGHTSIZING_MODIFY','SAVINGS_PLAN_OPPORTUNITY'];
              const costFindings = findings.filter(f => costKeys.some(k => (f.type || '').toUpperCase().includes(k)));
              const seen = new Set();
              const deduped = costFindings.filter(f => { if (seen.has(f.type)) return false; seen.add(f.type); return true; });
              if (!deduped.length) return (
                <div style={card({ textAlign: 'center', padding: 48 })}>
                  <p style={{ color: C.muted, margin: 0 }}>No cost optimisation opportunities detected. Run a scan to check.</p>
                </div>
              );
              return (
                <div style={card({ padding: 0, overflow: 'hidden' })}>
                  <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr 130px', background: C.surface2, borderBottom: `1px solid ${C.border}`, padding: '10px 18px' }}>
                    {['SERVICE', 'OPPORTUNITY', 'EST. SAVING/MO'].map(h => (
                      <div key={h} style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.07em' }}>{h}</div>
                    ))}
                  </div>
                  {deduped.map((f, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '180px 1fr 130px', padding: '13px 18px', borderBottom: `1px solid ${C.borderDim}`, background: i % 2 === 0 ? C.surface : C.bg, alignItems: 'start' }}>
                      <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{humaniseType(f.type || '')}</div>
                      <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, paddingRight: 20 }}>{f.recommendation || f.description || '—'}</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: f.estimated_monthly_saving > 0 ? C.low : C.muted }}>
                        {f.estimated_monthly_saving > 0 ? `$${f.estimated_monthly_saving.toLocaleString()}` : '—'}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            COMPLIANCE
        ══════════════════════════════════════════════════════════════ */}
        {tab === 'compliance' && (
          <div>
            <h2 style={{ margin: '0 0 18px', fontSize: 20, fontWeight: 700, color: C.white }}>Compliance Posture</h2>
            {(() => {
              const hasCloudTrail = findings.some(f => (f.type || '').toUpperCase().includes('CLOUDTRAIL'));
              const hasEBS = findings.some(f => (f.type || '').toUpperCase().includes('EBS'));
              const hasCritical = (scan?.critical_findings || 0) > 0;
              const frameworks = [
                { name: 'GDPR',    status: hasCloudTrail ? 'At Risk' : 'Compliant',       action: 'Enable multi-region CloudTrail for a full audit trail' },
                { name: 'SOC 2',   status: hasCritical   ? 'At Risk' : 'Monitoring',       action: `Resolve ${scan?.critical_findings || 0} critical issues for SOC 2 readiness` },
                { name: 'HIPAA',   status: hasEBS        ? 'At Risk' : 'Compliant',        action: 'Encrypt all EBS volumes to protect PHI at rest' },
                { name: 'PCI DSS', status: 'Not Applicable',                               action: 'No cardholder data detected — re-evaluate if payments are added' },
              ];
              const sCol = (s) => s === 'At Risk' ? C.critical : s === 'Compliant' ? C.low : C.muted;
              return (
                <>
                  <div style={card({ padding: 0, overflow: 'hidden', marginBottom: 24 })}>
                    <div style={{ display: 'grid', gridTemplateColumns: '110px 140px 1fr', background: C.surface2, borderBottom: `1px solid ${C.border}`, padding: '10px 18px' }}>
                      {['FRAMEWORK', 'STATUS', 'ACTION REQUIRED'].map(h => (
                        <div key={h} style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.07em' }}>{h}</div>
                      ))}
                    </div>
                    {frameworks.map((fw, i) => (
                      <div key={i} style={{ display: 'grid', gridTemplateColumns: '110px 140px 1fr', padding: '14px 18px', borderBottom: i < frameworks.length - 1 ? `1px solid ${C.borderDim}` : 'none', background: i % 2 === 0 ? C.surface : C.bg, alignItems: 'center' }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: C.white }}>{fw.name}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: sCol(fw.status), display: 'inline-block', boxShadow: fw.status === 'At Risk' ? `0 0 8px ${C.critical}` : fw.status === 'Compliant' ? `0 0 8px ${C.low}` : 'none' }} />
                          <span style={{ fontSize: 13, fontWeight: 700, color: sCol(fw.status) }}>{fw.status}</span>
                        </div>
                        <div style={{ fontSize: 13, color: C.muted }}>{fw.action}</div>
                      </div>
                    ))}
                  </div>

                  <h3 style={{ margin: '0 0 14px', fontSize: 16, fontWeight: 700, color: C.white }}>Recommended Roadmap</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                    {[
                      { week: 'Week 1',   color: C.critical, items: ['Close open security groups', 'Remove public S3 ACLs', 'Enable CloudTrail & root MFA'] },
                      { week: 'Weeks 2–4', color: C.high,   items: ['Enable EBS default encryption', 'Add S3 lifecycle policies', 'Rightsize EC2 instances'] },
                      { week: 'Ongoing',  color: C.teal,    items: ['Daily automated scans', 'Re-run report after each fix', 'Monitor compliance drift'] },
                    ].map((phase, i) => (
                      <div key={i} style={card({ borderTop: `3px solid ${phase.color}`, marginBottom: 0, padding: 18, position: 'relative', overflow: 'hidden' })}>
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 50, background: `radial-gradient(ellipse at 50% 0%, ${phase.color}15 0%, transparent 70%)`, pointerEvents: 'none' }} />
                        <div style={{ fontSize: 12, fontWeight: 700, color: phase.color, letterSpacing: '0.06em', marginBottom: 12 }}>{phase.week.toUpperCase()}</div>
                        {phase.items.map((item, j) => (
                          <div key={j} style={{ fontSize: 13, color: C.muted, padding: '6px 0', borderBottom: j < phase.items.length - 1 ? `1px solid ${C.borderDim}` : 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ color: phase.color, fontSize: 16, lineHeight: 1 }}>›</span>{item}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            HISTORY
        ══════════════════════════════════════════════════════════════ */}
        {tab === 'history' && (
          <div>
            <h2 style={{ margin: '0 0 18px', fontSize: 20, fontWeight: 700, color: C.white }}>Scan History</h2>
            {history.length === 0 ? (
              <div style={card({ textAlign: 'center', padding: 48 })}>
                <p style={{ color: C.muted, margin: 0 }}>No scan history yet.</p>
              </div>
            ) : (
              <div style={card({ padding: 0, overflow: 'hidden' })}>
                <div style={{ display: 'grid', gridTemplateColumns: '170px 80px 65px 65px 65px 65px 110px 120px', background: C.surface2, borderBottom: `1px solid ${C.border}`, padding: '10px 18px' }}>
                  {['DATE', 'SCORE', 'CRIT', 'HIGH', 'MED', 'LOW', 'SAVINGS', ''].map((h, i) => (
                    <div key={i} style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.07em' }}>{h}</div>
                  ))}
                </div>
                {history.map((s, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '170px 80px 65px 65px 65px 65px 110px 120px', padding: '12px 18px', borderBottom: i < history.length - 1 ? `1px solid ${C.borderDim}` : 'none', background: i % 2 === 0 ? C.surface : C.bg, alignItems: 'center' }}>
                    {/* ── FIXED history date ───────────────────────────────── */}
                    <div style={{ fontSize: 13, color: C.muted }}>{fmtDateShort(s.created_at)}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: scoreColor(s.security_score || 0) }}>{s.security_score ?? '—'}</div>
                    <div style={{ fontSize: 13, color: (s.critical_findings || 0) > 0 ? C.critical : C.muted, fontWeight: (s.critical_findings || 0) > 0 ? 700 : 400 }}>{s.critical_findings ?? 0}</div>
                    <div style={{ fontSize: 13, color: (s.high_findings || 0) > 0 ? C.high : C.muted }}>{s.high_findings ?? 0}</div>
                    <div style={{ fontSize: 13, color: C.muted }}>{s.medium_findings ?? 0}</div>
                    <div style={{ fontSize: 13, color: C.muted }}>{s.low_findings ?? 0}</div>
                    <div style={{ fontSize: 13, color: C.low, fontWeight: 600 }}>${(s.monthly_savings || 0).toLocaleString()}</div>
                    <div>
                      {s.scan_id && (
                        <button onClick={() => handleDownloadPDF(s.scan_id, true, i)} disabled={histPdfLoading[i]} style={ghostBtn({ padding: '6px 14px', fontSize: 12, borderRadius: 7 })}>
                          {histPdfLoading[i] ? '...' : '↓ PDF'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            AWS SETUP
        ══════════════════════════════════════════════════════════════ */}
        {tab === 'aws' && (
          <div style={{ maxWidth: 680 }}>
            <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 700, color: C.white }}>AWS Setup</h2>
            <p style={{ color: C.muted, fontSize: 14, margin: '0 0 24px', lineHeight: 1.6 }}>Connect your AWS account via CloudFormation — takes under 2 minutes. We never store your credentials.</p>

            {awsInfo && (
              <>
                <div style={card({ borderLeft: `3px solid ${C.teal}`, background: C.surface2 })}>
                  <div style={{ fontSize: 11, color: C.teal, fontWeight: 700, letterSpacing: '0.08em', marginBottom: 14 }}>STEP 1 — DEPLOY CLOUDFORMATION STACK</div>
                  <p style={{ fontSize: 14, color: C.muted, margin: '0 0 14px', lineHeight: 1.7 }}>Click below — AWS will open with everything pre-filled. Just click <strong style={{ color: C.text }}>Create Stack</strong>.</p>
                  <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 14px', fontFamily: 'monospace', fontSize: 13, color: C.muted, marginBottom: 16 }}>
                    External ID: <span style={{ color: C.teal }}>{awsInfo.external_id}</span>
                  </div>
                  <a href={awsInfo.cloudformation_url} target="_blank" rel="noopener noreferrer" style={{ ...gradBtn(), display: 'inline-block', textDecoration: 'none' }}>
                    Launch CloudFormation →
                  </a>
                </div>

                <div style={card({ borderLeft: `3px solid ${C.teal}`, background: C.surface2 })}>
                  <div style={{ fontSize: 11, color: C.teal, fontWeight: 700, letterSpacing: '0.08em', marginBottom: 14 }}>STEP 2 — PASTE ROLE ARN</div>
                  <p style={{ fontSize: 14, color: C.muted, margin: '0 0 14px' }}>From CloudFormation → Outputs tab, copy the <strong style={{ color: C.text }}>RoleArn</strong> value.</p>
                  <form onSubmit={handleConnectAws}>
                    <label style={fieldLabel}>Role ARN</label>
                    <input style={inp} placeholder="arn:aws:iam::123456789012:role/SpectaineRole" value={roleArn} onChange={e => setRoleArn(e.target.value)} required />
                    <button type="submit" style={gradBtn()}>Connect AWS Account</button>
                  </form>
                </div>

                <div style={card()}>
                  <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: '0.08em', marginBottom: 14 }}>SLACK NOTIFICATIONS (OPTIONAL)</div>
                  <form onSubmit={handleSlack}>
                    <label style={fieldLabel}>Webhook URL</label>
                    <input style={inp} placeholder="https://hooks.slack.com/services/..." value={slackUrl} onChange={e => setSlackUrl(e.target.value)} />
                    <button type="submit" style={ghostBtn()}>Save Webhook</button>
                  </form>
                </div>
              </>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            SETTINGS
        ══════════════════════════════════════════════════════════════ */}
                {tab === 'settings' && (
          <div style={{ maxWidth: 580 }}>
            <h2 style={{ margin: '0 0 22px', fontSize: 20, fontWeight: 700, color: C.white }}>Settings</h2>

            <div style={card()}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: '0.08em', marginBottom: 16 }}>ACCOUNT</div>
              <form onSubmit={handleUpdateAccount}>
                <label style={fieldLabel}>Full Name</label>
                <input style={inp} value={accForm.full_name} onChange={e => setAccForm({ ...accForm, full_name: e.target.value })} />
                <label style={fieldLabel}>Company</label>
                <input style={inp} value={accForm.company} onChange={e => setAccForm({ ...accForm, company: e.target.value })} />
                <button type="submit" style={gradBtn()}>Save Changes</button>
              </form>
            </div>

            <div style={card()}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: '0.08em', marginBottom: 16 }}>CHANGE PASSWORD</div>
              <form onSubmit={handleChangePassword}>
                <label style={fieldLabel}>Current Password</label>
                <input style={inp} type="password" value={pwForm.old_password} onChange={e => setPwForm({ ...pwForm, old_password: e.target.value })} />
                <label style={fieldLabel}>New Password</label>
                <input style={inp} type="password" value={pwForm.new_password} onChange={e => setPwForm({ ...pwForm, new_password: e.target.value })} />
                <button type="submit" style={gradBtn()}>Update Password</button>
              </form>
            </div>

            <div style={card()}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: '0.08em', marginBottom: 16 }}>BILLING</div>
              <p style={{ fontSize: 14, color: C.muted, margin: '0 0 14px' }}>
                Status: <span style={{ color: billing?.subscription_active ? C.low : C.critical, fontWeight: 700, textTransform: 'uppercase' }}>{billing?.subscription_status || 'inactive'}</span>
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                {!billing?.subscription_active && <button onClick={handleSubscribe} style={gradBtn()}>Subscribe</button>}
                {billing?.subscription_active && <button onClick={handleBillingPortal} style={ghostBtn()}>Manage Billing →</button>}
              </div>
            </div>

            <div style={card({ border: `1px solid ${C.critical}30` })}>
              <div style={{ fontSize: 11, color: C.critical, fontWeight: 700, letterSpacing: '0.08em', marginBottom: 12 }}>DANGER ZONE</div>
              <p style={{ fontSize: 13, color: C.muted, margin: '0 0 14px', lineHeight: 1.6 }}>Export all your data (GDPR) or permanently delete your account and all scan history.</p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={handleExportData} style={ghostBtn()}>Export Data</button>
                <button 
                  onClick={async () => {
                    if (!window.confirm('Permanently delete your account and all data? This cannot be undone.')) return;
                    const password = prompt('Enter your password to confirm deletion:');
                    if (!password) return;
                    try {
                      const fd = new FormData();
                      fd.append('password', password);
                      const res = await fetch(`${API_URL}/account/delete`, {
                        method: 'DELETE',
                        headers: authHeaders(),
                        body: fd
                      });
                      if (!res.ok) {
                        const e = await res.json().catch(() => ({}));
                        throw new Error(e.detail || 'Delete failed');
                      }
                      localStorage.clear();
                      navigate('/');
                    } catch (e) {
                      flash(`❌ ${e.message}`);
                    }
                  }} 
                  style={ghostBtn({ borderColor: C.critical + '50', color: C.critical })}
                >
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default DashboardPage;